import { useMemo } from 'react';
import type {
  WorkbookDefinition,
  WorkbookDiagnostic,
  WorkbookRun,
  WorkbookRunMode,
} from '@erp/shared-types';
import type { WorkbookPlan } from '@erp/workbooks';
import type { FrontendToolDefinition } from '@/hooks/useFrontendTool';
import { useAgentModule } from '@/hooks/useAgentModule';
import { useWorkbookAgentReadTools } from './useWorkbookAgentReadTools';
import { useWorkbookAgentProfileTool } from './useWorkbookAgentProfileTool';
import { useWorkbookAgentSampleTools } from './useWorkbookAgentSampleTools';
import { useWorkbookAgentWriteTools } from './useWorkbookAgentWriteTools';
import { useWorkbookAgentAppTools } from './useWorkbookAgentAppTools';
import { useWorkbookAgentStatefulTools } from './useWorkbookAgentStatefulTools';

/** The latest Definition with the Edit token that later applies must present. */
export interface WorkbookSnapshot {
  readonly definition: WorkbookDefinition;
  /** Opaque; changes whenever an edit is accepted. Sent to the agent as `editVersion`. */
  readonly token: number;
  readonly revision: number;
}

/** What the agent tools may do to the Workbook editor, and nothing more. */
export interface WorkbookAgentPort {
  snapshot: () => WorkbookSnapshot;
  /** The edited Definition's Diagnostics, planned live (unsaved edits included). */
  diagnostics: () => readonly WorkbookDiagnostic[];
  /** Plans another Definition (an untried edit) as `diagnostics` plans the edited one. */
  plan: (definition: WorkbookDefinition) => WorkbookPlan;
  /**
   * Replaces the Definition as one edit (one step of the edit history) and
   * clears review status on `appliedStepIds`, but only when no edit was
   * accepted since `token` was read. Returns false when stale.
   */
  applyIfUnchanged: (
    token: number,
    definition: WorkbookDefinition,
    appliedStepIds: readonly string[],
  ) => boolean;
  /** Saves, resolving to the saved Definition's Diagnostics. They never block the save. */
  save: () => Promise<readonly WorkbookDiagnostic[]>;
  /** Starts a Run on the Target: a Worksheet, or an Output for up-to. */
  run: (mode: WorkbookRunMode, targetId: string | null) => Promise<WorkbookRun>;
  /**
   * Called after every applied agent edit: shows the user the tab it changed
   * (if they follow the agent), or null when it changed none. `removed` lists
   * the tabs the edit deleted, so a null id can tell navigation the agent
   * caused (the selected tab vanished) from an edit that left the view alone.
   */
  follow?: (id: string | null, removed?: readonly string[]) => void;
}

/**
 * What the Workbook page adds around the editor: the tab the user is on and
 * the tools that act on the page (navigation, SOP updates).
 */
export interface WorkbookAgentWorkspace {
  /** The tab shown: an item id, or 'process', 'runs', 'automations', 'versions', 'settings'. */
  selected: string;
  opened: readonly string[];
  tools: readonly FrontendToolDefinition[];
}

export function useWorkbookAgentTools({
  workbookId,
  port,
  workspace,
}: {
  workbookId: string;
  port: WorkbookAgentPort;
  workspace?: WorkbookAgentWorkspace | null;
}) {
  const reads = useWorkbookAgentReadTools(workbookId, port);
  const profile = useWorkbookAgentProfileTool(workbookId, port);
  const writes = useWorkbookAgentWriteTools(workbookId, port);
  const samples = useWorkbookAgentSampleTools(workbookId, port);
  const apps = useWorkbookAgentAppTools(workbookId, port);
  const statefulTable = useWorkbookAgentStatefulTools(workbookId, port);
  const pageTools = workspace?.tools;
  useAgentModule({
    moduleKey: 'workbooks',
    currentPage: `workbook:${workbookId}`,
    scope: `workbook:${workbookId}`,
    forcePanelEnabled: true,
    getModuleContext: () => ({
      workbookId,
      name: port.snapshot().definition.name,
      ...(workspace && { selectedTab: workspace.selected }),
    }),
    tools: useMemo(
      () => [
        ...reads,
        profile,
        ...writes,
        ...samples,
        ...apps,
        statefulTable,
        ...(pageTools ?? []),
      ],
      [reads, profile, writes, samples, apps, statefulTable, pageTools],
    ),
  });
}
